import { useEffect, useState } from "react";
import { usePageState } from "@/contexts/NavigationContext";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import DocumentCenter from "@/components/DocumentCenter";
import ExcelBulkActions from "@/components/ExcelBulkActions";
import { PlusIcon, PencilSimpleIcon, FileTextIcon, StarIcon, TruckIcon, IdentificationCardIcon } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useLiveSearch, SearchBox } from "@/components/LiveSearch";

const TIPOS = ["nacional", "exterior", "natural"];

const empty = {
  codigo: "", nombre: "", rtn: "", tipo: "nacional", contacto: "",
  telefono: "", email: "", direccion: "", dias_credito: 30, limite_credito: 0,
  cuenta_contable: "2101", moneda: "HNL", evaluacion: 5, notas: "", prefijo_producto: "",
};

export default function Suppliers() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = usePageState("q", "");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [stmt, setStmt] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/suppliers"); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const busqueda = useLiveSearch(items, (s) => [s.codigo, s.nombre, s.rtn, s.tipo, s.pais, s.contacto, s.email, s.telefono], { estado: [q, setQ] });

  const onSave = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, dias_credito: Number(form.dias_credito), limite_credito: Number(form.limite_credito), evaluacion: Number(form.evaluacion) };
      if (editing) { await api.put(`/suppliers/${editing.id}`, payload); toast.success("Proveedor actualizado"); }
      else { await api.post("/suppliers", payload); toast.success("Proveedor creado"); }
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const pm = useModalForm({ value: form, abierto: open, onClose: () => setOpen(false), onSave });
  const em = useModalForm({ abierto: Boolean(stmt), onClose: () => setStmt(null) });

  const totalDeuda = items.reduce((s, i) => s + i.saldo, 0);

  return (
    <Layout
      title="Proveedores"
      subtitle={`${items.length} proveedores activos · Deuda total: ${fmtL(totalDeuda)}`}
      actions={
        <div className="flex items-center gap-2">
          <ExcelBulkActions
            templateUrl="/suppliers/import/template"
            exportUrl="/suppliers/export"
            importUrl="/suppliers/import"
            templateFilename="plantilla_proveedores_agroport.xlsx"
            exportFilename="proveedores_export.xlsx"
            resultLabels={{ created: "Importados", updated: "Saltados" }}
            onImported={load}
            testidPrefix="suppliers"
          />
          <button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="new-supplier-btn"
            className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2 transition-all active:scale-95">
            <PlusIcon size={16} weight="bold" /> Nuevo proveedor
          </button>
        </div>
      }
    >
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border/60 flex gap-3">
          <SearchBox busqueda={busqueda} placeholder="Buscar por nombre, RTN, código, país o contacto…" testid="suppliers-search" label="Buscar proveedores" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-3 pl-5">Código</th><th className="p-3">Proveedor</th>
                <th className="p-3">RTN</th><th className="p-3">Tipo</th>
                <th className="p-3">Días</th><th className="p-3 text-right">Límite</th>
                <th className="p-3 text-right">Saldo</th><th className="p-3">Eval</th>
                <th className="p-3 pr-5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="9" className="p-8 text-center text-text-muted">Cargando…</td></tr>
              : busqueda.resultados.length === 0 ? <tr><td colSpan="9" className="p-12 text-center text-text-muted">{busqueda.activa ? `Sin proveedores que coincidan con «${busqueda.texto}».` : "Sin proveedores"}</td></tr>
              : busqueda.resultados.map((s) => (
                <tr key={s.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`supplier-row-${s.codigo}`}>
                  <td className="p-3 pl-5 font-mono text-xs text-agro-green">{s.codigo}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <TruckIcon size={14} className="text-text-muted" weight="duotone" />
                      <span className="text-sm">{s.nombre}</span>
                    </div>
                    <div className="text-[11px] text-text-muted ml-6">{s.contacto || "—"} · {s.telefono || "sin teléfono"}</div>
                  </td>
                  <td className="p-3 font-mono text-xs text-text-secondary">{s.rtn || "—"}</td>
                  <td className="p-3"><span className="text-[10px] uppercase px-2 py-0.5 rounded bg-app border border-border text-text-secondary">{s.tipo}</span></td>
                  <td className="p-3 text-xs text-text-secondary">{s.dias_credito}d</td>
                  <td className="p-3 text-right text-sm">{fmtL(s.limite_credito)}</td>
                  <td className="p-3 text-right text-sm font-medium text-agro-yellow">{fmtL(s.saldo)}</td>
                  <td className="p-3">
                    <div className="flex gap-0.5">{[1,2,3,4,5].map(n => <StarIcon key={n} size={11} weight={n <= s.evaluacion ? "fill" : "regular"} className={n <= s.evaluacion ? "text-agro-yellow" : "text-text-muted"} />)}</div>
                  </td>
                  <td className="p-3 pr-5 text-right">
                    <button onClick={() => navigate(`/proveedores/${s.id}`)} title="Perfil: consolidado y subcuentas por procedencia" className="p-1.5 text-text-muted hover:text-agro-green" data-testid={`supplier-profile-${s.codigo}`}><IdentificationCardIcon size={16} /></button>
                    <button onClick={async () => { try { const { data } = await api.get(`/suppliers/${s.id}/statement`); setStmt(data); } catch (e) { toast.error(apiErr(e)); } }} className="p-1.5 text-text-muted hover:text-agro-yellow" data-testid={`supplier-statement-${s.codigo}`}><FileTextIcon size={16} /></button>
                    <button aria-label="Editar" onClick={() => { setEditing(s); setForm({ ...empty, ...s }); setOpen(true); }} className="p-1.5 text-text-muted hover:text-agro-green"><PencilSimpleIcon size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...pm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="supplier-modal">
            <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
              <h3 className="font-heading text-lg font-semibold">{editing ? "Editar proveedor" : "Nuevo proveedor"}</h3>
              <BotonCerrar onClick={pm.cerrar} />
            </div>
            <form onSubmit={pm.guardar} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Código *" v={form.codigo} on={(v) => setForm({...form, codigo:v})} required testid="supplier-codigo" />
              <F label="Nombre *" v={form.nombre} on={(v) => setForm({...form, nombre:v})} required testid="supplier-nombre" />
              <F label="RTN" v={form.rtn} on={(v) => setForm({...form, rtn:v})} />
              <Sel label="Tipo" v={form.tipo} on={(v) => setForm({...form, tipo:v})} opts={TIPOS} />
              <F label="Contacto" v={form.contacto} on={(v) => setForm({...form, contacto:v})} />
              <F label="Teléfono" v={form.telefono} on={(v) => setForm({...form, telefono:v})} />
              <F label="Email" v={form.email} on={(v) => setForm({...form, email:v})} />
              <F label="Dirección" v={form.direccion} on={(v) => setForm({...form, direccion:v})} />
              <F label="Días crédito" type="number" v={form.dias_credito} on={(v) => setForm({...form, dias_credito:v})} />
              <F label="Límite crédito (L.)" type="number" v={form.limite_credito} on={(v) => setForm({...form, limite_credito:v})} />
              <F label="Cuenta contable" v={form.cuenta_contable} on={(v) => setForm({...form, cuenta_contable:v})} />
              <Sel label="Moneda" v={form.moneda} on={(v) => setForm({...form, moneda:v})} opts={["HNL","USD","EUR"]} />
              <F label="Prefijo de códigos de producto (ej. SEM → SEM-041)" v={form.prefijo_producto || ""} on={(v) => setForm({...form, prefijo_producto: v.toUpperCase()})} testid="supplier-prefijo" />
              <Sel label="Evaluación" v={form.evaluacion} on={(v) => setForm({...form, evaluacion: Number(v)})} opts={[1,2,3,4,5]} />
              <div className="md:col-span-2"><F label="Notas" v={form.notas} on={(v) => setForm({...form, notas:v})} /></div>
              <div className="md:col-span-2 flex justify-end gap-3 pt-3 border-t border-border/60">
                <button type="button" onClick={pm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
                <button type="submit" data-testid="supplier-save-btn" disabled={pm.saving} className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md hover:bg-agro-green-hover disabled:opacity-40">{pm.saving ? "Guardando…" : "Guardar"}</button>
              </div>
            </form>
            {editing && (
              <div className="p-5 border-t border-border/60">
                <DocumentCenter
                  entityType="supplier"
                  entityId={editing.id}
                  title="Documentos del proveedor (RTN, certificados, contratos)"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {stmt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...em.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-border flex justify-between sticky top-0 bg-card">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-text-muted">Estado de cuenta proveedor</div>
                <h3 className="font-heading text-lg font-semibold">{stmt.supplier.nombre}</h3>
                <div className="text-xs text-text-muted">RTN {stmt.supplier.rtn || "—"} · Saldo: <span className="text-agro-yellow font-medium">{fmtL(stmt.supplier.saldo)}</span></div>
              </div>
              <BotonCerrar onClick={em.cerrar} />
            </div>
            <div className="p-5 space-y-5">
              <div>
                <h4 className="font-heading text-sm uppercase tracking-wider text-text-muted mb-2">Facturas ({stmt.invoices.length})</h4>
                <table className="w-full">
                  <thead><tr className="text-left text-[10px] uppercase text-text-muted border-b border-border/40">
                    <th className="p-2">Número</th><th className="p-2">Fecha</th><th className="p-2">Vence</th>
                    <th className="p-2">Estado</th><th className="p-2 text-right">Total</th><th className="p-2 text-right">Saldo</th>
                  </tr></thead>
                  <tbody>
                    {stmt.invoices.map((f) => (
                      <tr key={f.id} className="border-b border-border/30">
                        <td className="p-2 font-mono text-xs text-agro-green">{f.numero_interno}</td>
                        <td className="p-2 text-xs">{fmtDate(f.fecha)}</td>
                        <td className="p-2 text-xs">{fmtDate(f.fecha_vencimiento)}</td>
                        <td className="p-2"><span className="text-[10px] uppercase px-2 py-0.5 rounded bg-app border border-border">{f.estado}</span></td>
                        <td className="p-2 text-right text-sm">{fmtL(f.total)}</td>
                        <td className="p-2 text-right text-sm text-agro-yellow">{fmtL(f.saldo_pendiente)}</td>
                      </tr>
                    ))}
                    {stmt.invoices.length === 0 && <tr><td colSpan="6" className="p-4 text-center text-text-muted text-sm">Sin facturas</td></tr>}
                  </tbody>
                </table>
              </div>
              <div>
                <h4 className="font-heading text-sm uppercase tracking-wider text-text-muted mb-2">Pagos realizados ({stmt.payments.length})</h4>
                <table className="w-full">
                  <thead><tr className="text-left text-[10px] uppercase text-text-muted border-b border-border/40">
                    <th className="p-2">Factura</th><th className="p-2">Fecha</th><th className="p-2">Método</th><th className="p-2 text-right">Monto</th>
                  </tr></thead>
                  <tbody>
                    {stmt.payments.map((p) => (
                      <tr key={p.id} className="border-b border-border/30">
                        <td className="p-2 font-mono text-xs text-agro-green">{p.factura_numero}</td>
                        <td className="p-2 text-xs">{fmtDate(p.fecha)}</td>
                        <td className="p-2 text-xs capitalize">{p.metodo}</td>
                        <td className="p-2 text-right text-sm text-agro-green">{fmtL(p.monto)}</td>
                      </tr>
                    ))}
                    {stmt.payments.length === 0 && <tr><td colSpan="4" className="p-4 text-center text-text-muted text-sm">Sin pagos</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function F({ label, v, on, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">{label}</label>
      <input data-testid={testid} type={type} value={v ?? ""} required={required} onChange={(e) => on(e.target.value)}
        className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
    </div>
  );
}
function Sel({ label, v, on, opts }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">{label}</label>
      <select value={v} onChange={(e) => on(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green">
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
